import {Map as MapLibre} from 'maplibre-gl';

import MapTileService from '@js/modules/maps/mapTileService';


const ElevationComputationMapModule = {
    _elevationComputationModel: {
        mapContainerId: 'map-elevation-computation',
        map: undefined,

        coordinateElevations: []
    },

    createElevationMap() {
        if (!document.getElementById(this._elevationComputationModel.mapContainerId)) {
            return;
        }

        this._elevationComputationModel.map = new MapLibre({
            container: this._elevationComputationModel.mapContainerId,
            style: MapTileService.mapPlannerStyle,
            attributionControl: false,
            interactive: false,
            hash: false
        });

        this._elevationComputationModel.map.once('style.load', () => {
            this._elevationComputationModel.map.setLayerZoomRange('hillshade', 6, 24);
            this._elevationComputationModel.map.setLayoutProperty('hillshade', 'visibility', 'visible');

            this._elevationComputationModel.map.setTerrain({
                'source': 'hillshade',
                'exaggeration': 1
            });
        });
    },

    // Callback return:
    // First parameter: coordinateElevations array of objects {lat: lng:, deltaDistance:, totalDistance:, elevation:}
    // Second parameter: simplifiedCoordinateElevations array of [totalDistance, elevation] (useful to compute elevation Apsis)
    computeElevation(callback) {
        const model = this._elevationComputationModel;

        if (!model.map || !this._mapModel.linePoints.length) {
            return;
        }

        // Elevations always come from the terrain DEM: routing is requested without elevation, so
        // linePoint.elevation is unset and we read queryTerrainElevation. On the very first draw the DEM
        // (style / terrain / tiles) may not be ready yet and queryTerrainElevation returns null → NaN
        // elevations. Re-query a few times until enough points resolve, so elevation-dependent outputs
        // (hiking duration, D+/D-, difficulty) get real data instead of a degraded/flat value.
        const query = (retries) => {
            model.coordinateElevations = [];

            let totalDistance = 0;
            let validCount = 0;

            this._mapModel.linePoints.forEach((linePoint) => {
                totalDistance += linePoint.deltaDistance;

                const elevation = Number.isFinite(linePoint.elevation)
                    ? Math.round(linePoint.elevation)
                    : parseInt(model.map.queryTerrainElevation([linePoint.latLng.lng, linePoint.latLng.lat]));

                if (Number.isFinite(elevation)) {
                    validCount++;
                }

                model.coordinateElevations.push({
                    lat: linePoint.latLng.lat,
                    lng: linePoint.latLng.lng,
                    deltaDistance: linePoint.deltaDistance,
                    totalDistance: totalDistance,
                    elevation: elevation
                });
            });

            const total = this._mapModel.linePoints.length;
            if (retries > 0 && (total === 0 || (validCount / total) < 0.5)) {
                setTimeout(() => query(retries - 1), 300);
                return;
            }

            const elevationGraph = model.coordinateElevations.map((coordElevation) => {
                return [coordElevation.totalDistance, coordElevation.elevation];
            });

            const simplifiedCoordinateElevations = this.simplifyLine(elevationGraph, {
                defaultTolerance: 0.1,
                convertToXY: true
            });

            callback(model.coordinateElevations, simplifiedCoordinateElevations);
        };

        // We need to set the map position to be able to compute terrain elevation.
        model.map.setCenter([this._mapModel.linePoints[0].latLng.lng, this._mapModel.linePoints[0].latLng.lat]);
        model.map.setZoom(12);

        // Wait for the navigation to settle, then query (retrying while the DEM warms up on first use).
        model.map.once('idle', () => query(5));
    },

    clearElevationMap() {
        if (!this._elevationComputationModel.map) {
            return;
        }

        this._elevationComputationModel.coordinateElevations = [];
    },

    destroyElevationMap() {
        if (!this._elevationComputationModel.map) {
            return;
        }

        this._elevationComputationModel.coordinateElevations = [];

        this._elevationComputationModel.map.remove();
    }

    /* Private Methods
    ******************** */
};

export default ElevationComputationMapModule;
